import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { formatCpf, formatCep } from '../common/validators';

/** Campos do responsável que vivem no cadastro da criança. */
export interface CamposResponsavelAluno {
  id: number;
  responsavel: string;
  telefone_responsavel: string;
  responsavel_email: string;
  responsavel_cpf: string;
  responsavel_rg: string;
  responsavel_profissao: string;
  responsavel_nacionalidade: string;
  responsavel_estado_civil: string;
  responsavel_endereco: string;
  responsavel_cep: string;
  responsavel_logradouro: string;
  responsavel_numero: string;
  responsavel_complemento: string;
  responsavel_bairro: string;
  responsavel_cidade: string;
  responsavel_estado: string;
  responsavel_parentesco: string;
}

interface ResponsavelFamilia {
  id: number;
  aluno_id: number;
  nome?: string;
  cpf?: string;
  rg?: string;
  endereco?: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
  parentesco?: string;
  profissao?: string;
  nacionalidade?: string;
  estado_civil?: string;
  guarda?: string | null;
  responsavel_financeiro?: boolean;
}

interface Props {
  item: Partial<CamposResponsavelAluno>;
  onChange: (campo: keyof CamposResponsavelAluno, valor: any) => void;
  buscandoCep: boolean;
  onBuscarCep: () => void;
}

/** Rótulo amigável de cada campo, para dizer ao gestor o que foi completado. */
const ROTULOS: Partial<Record<keyof CamposResponsavelAluno, string>> = {
  responsavel: 'nome',
  responsavel_cpf: 'CPF',
  responsavel_rg: 'RG',
  telefone_responsavel: 'telefone',
  responsavel_email: 'e-mail',
  responsavel_parentesco: 'parentesco',
  responsavel_endereco: 'endereço',
  responsavel_profissao: 'profissão',
  responsavel_nacionalidade: 'nacionalidade',
  responsavel_estado_civil: 'estado civil',
};

const vazio = (v: unknown) => !String(v ?? '').trim();

/**
 * Bloco "Responsável Legal" do cadastro da criança.
 *
 * Identificada a criança, os dados do responsável são completados
 * AUTOMATICAMENTE a partir do que a creche já informou em Famílias (o mesmo
 * responsável vinculado à matrícula). Só preenche campo vazio — nada digitado
 * aqui é sobrescrito — e diz na tela o que veio de onde.
 */
export default function BlocoResponsavelAluno({ item, onChange, buscandoCep, onBuscarCep }: Props) {
  const idAluno = item.id;
  const [autoMsg, setAutoMsg] = useState('');

  // O efeito roda por criança (id), não a cada tecla: lê os valores atuais por ref.
  const itemRef = useRef(item);
  itemRef.current = item;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const completarDaFamilia = useCallback(async (avisarSemFonte: boolean) => {
    if (!idAluno) return;
    let lista: ResponsavelFamilia[] = [];
    try {
      const { data } = await api.get('/familias/responsaveis');
      lista = Array.isArray(data) ? data : data?.data || [];
    } catch {
      return; // sem acesso ao módulo Famílias: cadastro segue manual
    }

    const doAluno = lista.filter((r) => Number(r.aluno_id) === Number(idAluno));
    if (!doAluno.length) {
      if (avisarSemFonte) {
        setAutoMsg('Nenhum responsável vinculado a esta criança em Famílias — preencha aqui e o contrato usará estes dados.');
      }
      return;
    }

    // Prioriza quem responde pelo financeiro, depois a guarda legal.
    const r =
      doAluno.find((x) => x.responsavel_financeiro) ||
      doAluno.find((x) => x.guarda === 'legal') ||
      doAluno[0];

    const deFamilia: Partial<Record<keyof CamposResponsavelAluno, string | undefined>> = {
      responsavel: r.nome,
      responsavel_cpf: r.cpf ? formatCpf(r.cpf) : undefined,
      responsavel_rg: r.rg,
      telefone_responsavel: r.telefone || r.whatsapp,
      responsavel_email: r.email,
      responsavel_parentesco: r.parentesco,
      responsavel_endereco: r.endereco,
      responsavel_profissao: r.profissao,
      responsavel_nacionalidade: r.nacionalidade,
      responsavel_estado_civil: r.estado_civil,
    };

    const completados: string[] = [];
    for (const [campo, valor] of Object.entries(deFamilia) as [keyof CamposResponsavelAluno, string | undefined][]) {
      if (!String(valor ?? '').trim()) continue;
      if (!vazio(itemRef.current[campo])) continue;
      onChangeRef.current(campo, valor);
      completados.push(ROTULOS[campo] || String(campo));
    }

    setAutoMsg(
      completados.length
        ? `✓ Completado automaticamente a partir de Famílias: ${completados.join(', ')}. Revise antes de salvar.`
        : 'Dados do responsável já conferem com o cadastro de Famílias.',
    );
  }, [idAluno]);

  useEffect(() => {
    setAutoMsg('');
    if (!idAluno) return;
    void completarDaFamilia(true);
  }, [idAluno, completarDaFamilia]);

  return (
    <>
      <h4 className="form-secao-titulo">Responsável Legal e Financeiro</h4>
      <p className="texto-ajuda">Dados completos para formalização do contrato de prestação de serviços.</p>

      {idAluno && (
        <p className="texto-ajuda" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>{autoMsg || 'Verificando dados do responsável na matrícula…'}</span>
          <button type="button" className="btn-secundario" onClick={() => completarDaFamilia(true)}>
            Recompletar da matrícula
          </button>
        </p>
      )}

      <div className="linha-campos linha-2">
        <label>Nome do Responsável
          <input value={item.responsavel ?? ''} onChange={(e) => onChange('responsavel', e.target.value)} />
        </label>
        <label>Parentesco/Relação
          <select value={item.responsavel_parentesco ?? ''} onChange={(e) => onChange('responsavel_parentesco', e.target.value)}>
            <option value="">Selecione...</option>
            <option value="Mãe">Mãe</option>
            <option value="Pai">Pai</option>
            <option value="Avô/Avó">Avô/Avó</option>
            <option value="Tutor Legal">Tutor Legal</option>
            <option value="Outro">Outro</option>
          </select>
        </label>
      </div>
      <div className="linha-campos linha-2">
        <label>CPF do Responsável
          <input value={item.responsavel_cpf ?? ''} onChange={(e) => onChange('responsavel_cpf', formatCpf(e.target.value))} placeholder="000.000.000-00" />
        </label>
        <label>RG do Responsável
          <input value={item.responsavel_rg ?? ''} onChange={(e) => onChange('responsavel_rg', e.target.value)} />
        </label>
      </div>
      <div className="linha-campos linha-2">
        <label>Profissão
          <input value={item.responsavel_profissao ?? ''} onChange={(e) => onChange('responsavel_profissao', e.target.value)} />
        </label>
        <label>Telefone/WhatsApp
          <input value={item.telefone_responsavel ?? ''} onChange={(e) => onChange('telefone_responsavel', e.target.value)} />
        </label>
      </div>
      <div className="linha-campos linha-2">
        <label>E-mail do Responsável
          <input type="email" value={item.responsavel_email ?? ''} onChange={(e) => onChange('responsavel_email', e.target.value)} placeholder="responsavel@email.com" />
        </label>
        <span className="texto-ajuda" style={{ alignSelf: 'center' }}>Usado para enviar o histórico de desenvolvimento, quando a creche optar por compartilhar.</span>
      </div>
      <div className="linha-campos linha-2">
        <label>Nacionalidade
          <input value={item.responsavel_nacionalidade ?? ''} onChange={(e) => onChange('responsavel_nacionalidade', e.target.value)} placeholder="Brasileira" />
        </label>
        <label>Estado Civil
          <select value={item.responsavel_estado_civil ?? ''} onChange={(e) => onChange('responsavel_estado_civil', e.target.value)}>
            <option value="">Selecione...</option>
            <option value="solteiro(a)">Solteiro(a)</option>
            <option value="casado(a)">Casado(a)</option>
            <option value="divorciado(a)">Divorciado(a)</option>
            <option value="viúvo(a)">Viúvo(a)</option>
            <option value="união estável">União estável</option>
          </select>
        </label>
      </div>
      <p className="texto-ajuda">Endereço do responsável — digite o CEP para preencher automaticamente; informe apenas número e complemento.</p>
      <div className="linha-campos linha-2">
        <label>CEP
          <input
            value={item.responsavel_cep ?? ''}
            onChange={(e) => onChange('responsavel_cep', formatCep(e.target.value))}
            onBlur={onBuscarCep}
            placeholder="00000-000"
          />
        </label>
        <label>
          {buscandoCep ? 'Buscando CEP...' : 'Logradouro'}
          <input value={item.responsavel_logradouro ?? ''} onChange={(e) => onChange('responsavel_logradouro', e.target.value)} />
        </label>
      </div>
      <div className="linha-campos linha-3">
        <label>Número
          <input value={item.responsavel_numero ?? ''} onChange={(e) => onChange('responsavel_numero', e.target.value)} />
        </label>
        <label>Complemento (se houver)
          <input value={item.responsavel_complemento ?? ''} onChange={(e) => onChange('responsavel_complemento', e.target.value)} />
        </label>
        <label>Bairro
          <input value={item.responsavel_bairro ?? ''} onChange={(e) => onChange('responsavel_bairro', e.target.value)} />
        </label>
      </div>
      <div className="linha-campos linha-2">
        <label>Cidade
          <input value={item.responsavel_cidade ?? ''} onChange={(e) => onChange('responsavel_cidade', e.target.value)} />
        </label>
        <label>UF
          <input
            value={item.responsavel_estado ?? ''}
            maxLength={2}
            onChange={(e) => onChange('responsavel_estado', e.target.value.toUpperCase())}
            placeholder="SP"
          />
        </label>
      </div>
    </>
  );
}
